import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Box, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCircleExclamation,
  faExclamationTriangle,
  faInbox,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
// commented out for potential future use
// import ReceiversPanel from "./ReceiversPanel";
import ReceiversTablePanel from "./ReceiversTablePanel";
import { formatLocalDate } from "../../utils/dateUtils";
import "../../style/MasterDetailView.css";

const MasterDetailView = ({ listings, darkMode, onAllocate, onDecline, initialSelectedId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedListingId, setSelectedListingId] = useState(null);
  const receiversPanelRef = useRef(null);

  // auto-select listing when initialSelectedId is set (from card view click)
  useEffect(() => {
    if (initialSelectedId) {
      setSelectedListingId(initialSelectedId);
    }
  }, [initialSelectedId]);

  // pre-sort listings: latest_request_date DESC, then pending_count DESC
  const sortedListings = useMemo(() => {
    const getLatestDate = (requests) => {
      if (!requests || requests.length === 0) return 0;
      const dates = requests
        .map((r) => r.created_at || r.date_created || r.request_date)
        .filter(Boolean)
        .map((d) => new Date(d).getTime())
        .filter((t) => !isNaN(t));
      return dates.length > 0 ? Math.max(...dates) : 0;
    };

    const sorted = [...listings].sort((a, b) => {
      // first: latest request date descending (most recent first)
      const dateA = getLatestDate(a.requests);
      const dateB = getLatestDate(b.requests);
      if (dateA !== dateB) {
        return dateB - dateA;
      }

      // second: pending count descending
      const pendingA = a.pending_count || 0;
      const pendingB = b.pending_count || 0;
      return pendingB - pendingA;
    });

    return sorted;
  }, [listings]);

  // find the selected listing
  const selectedListing = useMemo(() => {
    return listings.find((l) => l.id === selectedListingId) || null;
  }, [listings, selectedListingId]);

  // auto-scroll to receivers table when a listing is selected
  useEffect(() => {
    if (selectedListingId && receiversPanelRef.current) {
      setTimeout(() => {
        receiversPanelRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [selectedListingId]);

  // check if pickup deadline is soon (within 7 days) or past
  const getDeadlineStatus = (dateString) => {
    if (!dateString) return "normal";
    const deadline = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "overdue";
    if (diffDays <= 7) return "soon";
    return "normal";
  };

  // check if listing has a request within the last 24 hours
  const hasRecentRequest = (requests) => {
    if (!requests || requests.length === 0) return false;
    const now = Date.now();
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

    return requests.some((r) => {
      const dateStr = r.created_at || r.date_created || r.request_date;
      if (!dateStr) return false;
      const requestTime = new Date(dateStr).getTime();
      return !isNaN(requestTime) && requestTime > twentyFourHoursAgo;
    });
  };

  // columns for the listings table
  const columns = useMemo(
    () => [
      {
        field: "view",
        headerName: "",
        width: 50,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={t("Go to Workspace")} placement="top">
            <Box
              className="view-listing-cell"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/donor/donate-materials?listing=${params.row.id}`);
              }}
            >
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "material",
        headerName: t("Material Type"),
        flex: 1.5,
        minWidth: 150,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const isNew = hasRecentRequest(params.row.requests);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>{params.value}</span>
              {isNew && (
                <Tooltip title={t("New request in last 24h")}>
                  <span>
                    <FontAwesomeIcon
                      icon={faCircleExclamation}
                      style={{ color: "#e53935", fontSize: "0.9rem" }}
                    />
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: "remaining_qty",
        headerName: t("Remaining Qty (lbs)"),
        flex: 1,
        minWidth: 165,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <span className="remaining-qty">{params.value.toLocaleString()}</span>
        ),
      },
      {
        field: "cost",
        headerName: t("Cost (CAD)"),
        flex: 0.8,
        minWidth: 105,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => `$${params.value.toFixed(2)}`,
      },
      {
        field: "latest_pickup",
        headerName: t("Pickup By"),
        flex: 1,
        minWidth: 115,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const status = getDeadlineStatus(params.value);
          return (
            <Box
              className={`deadline-cell ${status}`}
              title={
                status === "overdue"
                  ? t("Overdue")
                  : status === "soon"
                  ? t("Expiring Soon")
                  : ""
              }
            >
              {status !== "normal" && (
                <FontAwesomeIcon
                  icon={status === "overdue" ? faExclamationTriangle : faClock}
                  className="deadline-icon"
                />
              )}
              {formatLocalDate(params.value)}
            </Box>
          );
        },
      },
      {
        field: "pending_count",
        headerName: t("Requests"),
        flex: 0.8,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const pending = params.row.pending_count || 0;
          const total = params.row.total_request_count || 0;

          if (total === 0) {
            return <span className="request-count none">0</span>;
          }

          if (pending > 0) {
            return <span className="request-count pending">{pending}</span>;
          }

          return <span className="request-count processed">{total}</span>;
        },
      },
    ],
    [t, navigate]
  );

  // handle row click
  const handleRowClick = (params) => {
    setSelectedListingId(params.row.id);
  };

  // get row class name
  const getRowClassName = (params) => {
    return params.row.id === selectedListingId ? "Mui-selected" : "";
  };

  // styles for the DataGrid
  const dataGridStyles = useMemo(
    () => ({
      border: "none",
      fontFamily: '"Lato", sans-serif',
      fontSize: "0.875rem",
      "& .MuiDataGrid-columnHeaders": {
        backgroundColor: darkMode ? "rgba(255,255,255,0.03)" : "#f5f5f5",
        borderBottom: darkMode
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid #e0e0e0",
        minHeight: "52px !important",
        position: "sticky",
        top: 0,
        zIndex: 10,
      },
      "& .MuiDataGrid-columnHeader": {
        backgroundColor: darkMode ? "rgba(30, 30, 30, 1)" : "#f5f5f5",
        overflow: "visible",
      },
      "& .MuiDataGrid-columnHeaderTitleContainer": {
        overflow: "visible",
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: darkMode ? "rgba(255, 255, 255, 0.7)" : "#666666",
        whiteSpace: "nowrap",
        overflow: "visible",
        textOverflow: "clip",
      },
      "& .MuiDataGrid-row": {
        cursor: "pointer",
      },
      "& .MuiDataGrid-row:nth-of-type(odd)": {
        backgroundColor: darkMode ? "transparent" : "#ffffff",
      },
      "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: darkMode ? "rgba(255, 255, 255, 0.02)" : "#f8f8f8",
      },
      "& .MuiDataGrid-row:hover": {
        backgroundColor: darkMode
          ? "rgba(102, 157, 46, 0.1) !important"
          : "rgba(102, 157, 46, 0.08) !important",
      },
      "& .MuiDataGrid-row.Mui-selected": {
        backgroundColor: darkMode
          ? "rgba(102, 157, 46, 0.15) !important"
          : "rgba(102, 157, 46, 0.1) !important",
      },
      "& .MuiDataGrid-row.Mui-selected:hover": {
        backgroundColor: darkMode
          ? "rgba(102, 157, 46, 0.2) !important"
          : "rgba(102, 157, 46, 0.15) !important",
      },
      "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
        borderBottom: darkMode
          ? "1px solid rgba(255, 255, 255, 0.04)"
          : "1px solid #f0f0f0",
      },
      "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
        outline: "none",
      },
      "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
        {
          outline: "none",
        },
    }),
    [darkMode]
  );

  return (
    <Box className={`master-detail-view ${darkMode ? "dark" : "light"}`}>
      {/* Top: Listings table */}
      <Box className="listings-panel">
        <DataGrid
          rows={sortedListings}
          columns={columns}
          onRowClick={handleRowClick}
          getRowClassName={getRowClassName}
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20 } },
          }}
          rowHeight={48}
          sx={dataGridStyles}
          slots={{
            noRowsOverlay: () => (
              <Box className="no-listings-overlay">
                <FontAwesomeIcon icon={faInbox} className="empty-icon" />
                <p>{t("No listings found")}</p>
              </Box>
            ),
          }}
        />
      </Box>

      {/* Bottom: Receivers table panel (shows when listing selected) */}
      <Box ref={receiversPanelRef} className="receivers-table-panel-container">
        <ReceiversTablePanel
          listing={selectedListing}
          darkMode={darkMode}
          onAllocate={onAllocate}
          onDecline={onDecline}
        />
      </Box>

      {/* 
        Commented out for potential future use
        The card-based panel was difficult to use with many receivers (100+)
        
        <Box className="receivers-panel-container">
          <ReceiversPanel
            listing={selectedListing}
            darkMode={darkMode}
            onAllocate={onAllocate}
            onDecline={onDecline}
          />
        </Box>
      */}
    </Box>
  );
};

MasterDetailView.propTypes = {
  listings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      material: PropTypes.string,
      quantity: PropTypes.number,
      remaining_qty: PropTypes.number,
      cost: PropTypes.number,
      latest_pickup: PropTypes.string,
      pending_count: PropTypes.number,
      total_request_count: PropTypes.number,
      requests: PropTypes.array,
    })
  ).isRequired,
  darkMode: PropTypes.bool,
  onAllocate: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  initialSelectedId: PropTypes.string,
};

export default MasterDetailView;
